import { isDebug, resolveApiBase } from './config'

/**
 * Thrown by the API client on any non-OK HTTP response and on SAP's
 * "200 OK with OData error envelope" anti-pattern. Extends `Error` so the
 * `err instanceof Error ? err.message : '<fallback>'` pattern used throughout
 * the apps surfaces the real SAP message instead of falling through.
 */
export class ApiError extends Error {
  readonly status: number
  readonly body?: unknown

  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/**
 * Detects SAP's "200 OK with error envelope" anti-pattern. SAP can return
 * HTTP 200 with `{ error: { ... } }` instead of a 4xx, so we sniff for it.
 */
export function isOdataError(payload: unknown): payload is { error: { message?: { value?: string } } } {
  if (!payload || typeof payload !== 'object') return false
  const err = (payload as { error?: unknown }).error
  return Boolean(err) && typeof err === 'object'
}

export async function apiGet<T>(path: string, params?: Record<string, string | number | undefined>): Promise<T> {
  const url = buildUrl(path, params)
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } })
  return handleResponse<T>(res, url)
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const url = buildUrl(path)
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
  return handleResponse<T>(res, url)
}

function buildUrl(path: string, params?: Record<string, string | number | undefined>): string {
  const base = resolveApiBase().replace(/\/$/, '')
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  const url = new URL(`${base}${cleanPath}`)
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      // SAP's Spring backend distinguishes "param present with empty value"
      // (OK) from "param missing entirely" (500 MissingServletRequestParameter).
      // HANA endpoints in particular require keys like `unit` / `lot` /
      // `houseNo` to be present even when the customer's address has none.
      // Only drop undefined / null — callers that genuinely want to omit a
      // key can pass undefined.
      if (v === undefined || v === null) continue
      url.searchParams.set(k, String(v))
    }
  }
  return url.toString()
}

async function handleResponse<T>(res: Response, url: string): Promise<T> {
  const text = await res.text()
  let body: unknown
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    body = text
  }

  if (isDebug()) {
    console.debug('[al-api]', res.status, url, body)
  }

  if (!res.ok) {
    throw new ApiError(res.status, extractMessage(body) ?? `HTTP ${res.status}`, body)
  }

  if (isOdataError(body)) {
    throw new ApiError(res.status, extractMessage(body) ?? 'SAP error', body)
  }

  return body as T
}

function extractMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined
  const errObj = (body as { error?: { message?: { value?: string } } }).error
  if (errObj && typeof errObj.message?.value === 'string') return errObj.message.value
  const plain = (body as { message?: unknown }).message
  return typeof plain === 'string' ? plain : undefined
}

/**
 * Best-effort POST to the WP order log. The React app fires and forgets;
 * a failure here is silent because SAP is the source of truth. The endpoint
 * is intentionally public/anonymous — no nonce is sent.
 */
export async function postOrderLog(payload: {
  app: 'spare_parts' | 'screens'
  order_number: string
  line_items: Array<{ sku: string; qty: number; price: number; name: string }>
  total: number
}): Promise<void> {
  const restUrl = typeof window !== 'undefined' ? window.AL_WINDOWS_CONFIG?.restUrl : undefined
  if (!restUrl) return
  try {
    await fetch(`${restUrl}order/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    })
  } catch {
    // Intentional: best-effort logging only.
  }
}
